import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
	getLister,
	getNextPage,
	updateCurrentPageNumber,
} from '../../actions/lister-actions';
import {
	useIsBelowXSmallOnly,
	useIsXSmallAndAbove,
} from '../../hooks/mediaQueries';
import { AcceptedRegions } from '../../utilities/locale-utilities';
import MiddleBanner from '../Banner/MiddleBanner/MiddleBanner';
import MobileFinanceBanner from '../Banner/MobileFinanceBanner';
import MobileFinanceBannerSwitch24 from '../Banner/MobileFinanceBannerSwitch24';
import DealsMobileProductCard from '../ProductCard/DealsMobileProductCard/DealsMobileProductcard@default';
import MobileProductCard from '../ProductCard/MobileProductCard/MobileProductCard@default';
import ProductCardMobileView from '../Views/ProductCardMobileView/ProductCardMobileView@default';
import Store from '../Views/store/store';
import DealsWeRecommendPod from './DealsWeRecommendPod';
import LoadMoreProductsButton from './LoadMoreProductsButton/LoadMoreProductsButton';
import NoResultsCard from './NoResultsCard';
import PageCounter from './PageCounter/PageCounter';

const InfiniteScroll = () => {
	const {
		isDealsPage,
		pages,
		pages: {
			NextPageUrl,
			PreviousPageUrl,
			CurrentPageIndex,
			PageSize,
			PagesCount,
			Pages,
			firstPage,
		} = {},
		products,
		productCardView,
		loading,
		drawer: { drawerOpen },
	} = useSelector((state) => state);

	const { ListerMobileMiddleBannerEnabled, CustomCategoriesEnabled } =
		useSelector((state) => state.experimentSwitches);

	const { ShouldShowSwitchV2 } = useSelector((state) => state.featureSwitches);

	const dispatch = useDispatch();

	const isBelowXSmallOnly = useIsBelowXSmallOnly();
	const isXSmallAndAbove = useIsXSmallAndAbove();

	const isInitialMount = useRef(true);

	const triggerNextPage = useCallback(() => {
		if (NextPageUrl === '') {
			return;
		}
		dispatch(getNextPage(NextPageUrl));
	}, [dispatch, NextPageUrl]);

	const getNext = useRef(triggerNextPage);
	const getPages = useRef(Pages);
	const isLoading = useRef(loading);

	useEffect(() => {
		getNext.current = triggerNextPage;
	}, [triggerNextPage]);

	useEffect(() => {
		getPages.current = pages;
	}, [pages]);

	useEffect(() => {
		isLoading.current = loading;
	}, [loading]);

	const handlePrevPageShouldBeLoaded = useCallback(() => {
		if (!isLoading.current && window.scrollY <= 0 && !drawerOpen) {
			dispatch(getLister(PreviousPageUrl));
		}
	}, [PreviousPageUrl, dispatch, drawerOpen]);

	const observer = useRef(
		new IntersectionObserver(
			(entries) => {
				const intersecting = entries.filter((entry) => entry.isIntersecting);

				intersecting.forEach(({ target }) => {
					const pageNum = parseInt(target.getAttribute('data-page'), 10);

					const currentPage = getPages.current.CurrentPageIndex;

					if (!isLoading.current && pageNum !== currentPage) {
						dispatch(updateCurrentPageNumber(pageNum));
					}
				});
			},
			{ threshold: 0.3 }
		)
	);

	// Update page url when page index changes
	useEffect(() => {
		if (isInitialMount.current) {
			return;
		}

		let { pathname, search } = window.location;

		//Product length check to allow .find() to work with 0 products
		let { Url = '' } =
			products?.length <= 0 ??
			Pages.find(({ Index }) => Index === CurrentPageIndex) ??
			{};

		const replacer = (match, p1, p2, p3, p4) => {
			if (CurrentPageIndex > 1) {
				return `${p1}${p2}=${(CurrentPageIndex - 1) * PageSize}${p4}`;
			}

			return `${p4 === '&' ? '?' : ''}`;
		};

		const newUrl = Url.replace(/(\?|&)(seite|page)=(\d+)(&)/, replacer);

		if (
			!window.localDev &&
			!isLoading.current &&
			!drawerOpen &&
			`${pathname}${search}` !== newUrl
		) {
			history.replaceState(history.state, null, newUrl);
		}
	}, [CurrentPageIndex, drawerOpen, Pages, PageSize]);

	const scrollToPreviousItem = () => {
		const selectedSku = localStorage.getItem('selectedSku');
		const elementToScrollTo = document.getElementById(selectedSku);

		if (elementToScrollTo) {
			setTimeout(() => {
				elementToScrollTo.scrollIntoView({
					block: 'center',
				});
				localStorage.removeItem('selectedSku');
			}, 0);
		}
	};

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;

			if (history.scrollRestoration !== 'auto') {
				scrollToPreviousItem();
			}
		}

		return () => {};
	}, []);

	useEffect(() => {
		const selectedSku = localStorage.getItem('selectedSku');
		if (
			!selectedSku &&
			!loading &&
			window.scrollY === 0 &&
			CurrentPageIndex === firstPage &&
			CurrentPageIndex > 1 &&
			history.scrollRestoration
		) {
			window.scrollTo(0, 10);
		}
	}, [loading, firstPage, CurrentPageIndex]);

	// Scroll Restoration if prev page exists
	useEffect(() => {
		if (CurrentPageIndex > 1 && CurrentPageIndex === firstPage) {
			window.addEventListener(
				'load',
				function () {
					history.scrollRestoration = 'manual';
					window.addEventListener('scroll', handlePrevPageShouldBeLoaded);
				},
				false
			);
		}

		return () => {
			window.removeEventListener('scroll', handlePrevPageShouldBeLoaded);
		};
	}, [CurrentPageIndex, firstPage, handlePrevPageShouldBeLoaded]);

	const bannerIndex = isBelowXSmallOnly ? 2 : 3;

	let featuredProducts = 0;

	if (isDealsPage && isXSmallAndAbove) {
		featuredProducts = products.reduce(
			(acc, item, index) =>
				index <= 1 && item.IsFeaturedProduct ? ++acc : acc,
			0
		);
	}

	if (products?.length <= 0 && !loading) {
		return <NoResultsCard />;
	}

	return (
		<section
			className="lister__product-list pb-4 overflow-x-hidden"
			data-tag-section={`${isDealsPage ? 'deals' : 'lister'} product list`}
			data-tag-viewtype={productCardView ? productCardView : 'mobile'}>
			<div
				className="finance-drawer-container"
				id="finance-drawer-portal-target"></div>
			<ul className="flex flex-wrap sm:px-2">
				<DealsWeRecommendPod featuredProducts={featuredProducts} />

				{products.map((product, index) => {
					if (isDealsPage && index < featuredProducts) {
						return null;
					}

					let listItems = [];
					const offset = products.length % PageSize;
					const pageNumber = Math.max(
						1,
						firstPage + (index - offset) / PageSize
					);

					if ((index === 5 && ListerMobileMiddleBannerEnabled) || isDealsPage) {
						listItems.push(
							<MiddleBanner mobile key={uuidv4()} index={index} />
						);
					}

					if (index === 1) {
						listItems.push(
							<div id="PLP-ConversationalSearch-Mid" className="px-2"></div>
						);
					}

					const productInfo = {
						key: `${index + 1}-${product.Code}`,
						index,
						product,
						observer: observer.current,
						page: Math.floor(pageNumber),
					};

					listItems.push(
						<Store state={{ loaded: index === 0 }}>
							{isDealsPage && <DealsMobileProductCard {...productInfo} />}
							{!isDealsPage &&
							(CustomCategoriesEnabled || product.ShouldShowEnhancedCard) ? (
								<ProductCardMobileView {...productInfo} />
							) : (
								<MobileProductCard {...productInfo} />
							)}
						</Store>
					);

					if (index === bannerIndex) {
						listItems.push(
							<AcceptedRegions AOL key={uuidv4()}>
								{ShouldShowSwitchV2 ? (
									<MobileFinanceBannerSwitch24 />
								) : (
									<MobileFinanceBanner />
								)}
							</AcceptedRegions>
						);
					}

					if (pageNumber > 1 && pageNumber % 1 === 0) {
						listItems.push(
							<PageCounter
								key={uuidv4()}
								currentPageNum={pageNumber}
								pagesCount={PagesCount}
							/>
						);
					}

					return listItems;
				})}

				<LoadMoreProductsButton />
			</ul>
		</section>
	);
};

export default InfiniteScroll;
